<template>
  <!--
    7.22 — O CALENDÁRIO REPENSADO (D28, D9). A grade de mês 7×6 atravessava a lombada do caderno e
    quebrava coluna (vetado pelo autor, 03/09) — e um jogador de pesca não pergunta "o que tem no
    dia 12": pergunta "o que está valendo HOJE, e o que vem aí". Então a página é uma AGENDA.

    Segunda passada (04/09): a agenda listava um DIA por linha, e um evento de quatro dias virava
    quatro barras cinzentas com o mesmo nome — a página inteira dizia "Bloqueio da Praia" quatro
    vezes e não dizia o que o bloqueio faz. Agora a unidade é o EVENTO, não o dia:

    - cada evento aparece UMA vez, com o período que ele cobre e quanto falta para acabar;
    - o EFEITO vem impresso ("Mapa bloqueado", "−40%") — o backend passou a mandar `tipoDeEfeito`
      e `numeroDoEfeito` na rota do calendário no mesmo dia, porque sem isso não havia o que dizer;
    - três seções na ordem em que interessam: no ar agora, próximos, já passou;
    - o que está no ar leva selo de latão e a arte emoldurada; o passado desbota.
  -->
  <div class="agenda">
    <header class="agenda__mes">
      <button
        type="button"
        class="agenda__nav"
        :aria-label="t('events.calendar.prevMonth')"
        @click="mesAnterior"
      >
        <FishIcon nome="chevron-left" tamanho="pequeno" />
      </button>

      <h2>
        {{ nomeDoMes }} <span class="agenda__ano t-numero">{{ ano }}</span>
      </h2>

      <button
        type="button"
        class="agenda__nav"
        :aria-label="t('events.calendar.nextMonth')"
        @click="proximoMes"
      >
        <FishIcon nome="chevron-right" tamanho="pequeno" />
      </button>
    </header>

    <p class="agenda__hoje">
      <FishIcon nome="clock" tamanho="miudo" />
      <span>{{ t('events.calendar.today') }}</span>
      <strong class="t-numero">{{ dataDeHoje }}</strong>
    </p>

    <div v-if="!eventos.length" class="agenda__vazio">
      <FishIcon nome="water" tamanho="pequeno" />
      <p>{{ t('events.calendar.noEvents') }}</p>
    </div>

    <section v-for="grupo in grupos" :key="grupo.chave" class="agenda__grupo">
      <p class="agenda__secao">
        <span>{{ grupo.rotulo }}</span>
        <span class="agenda__contagem t-numero">{{ grupo.itens.length }}</span>
      </p>

      <ul>
        <li v-for="evento in grupo.itens" :key="evento.id">
          <button
            type="button"
            class="agenda__cartao"
            :class="[
              `agenda__cartao--${evento.tipo}`,
              { 'agenda__cartao--aberto': evento.id === abertoId, 'agenda__cartao--passado': evento.estado === 'past' },
            ]"
            @click="abrir(evento)"
          >
            <span class="agenda__arte">
              <img :src="getItemImage(evento.nome)" :alt="''" aria-hidden="true" />
            </span>

            <span class="agenda__texto">
              <span class="agenda__nome">{{ evento.nome }}</span>
              <span class="agenda__efeito">
                {{ evento.efeito }}
                <b v-if="evento.intensidade" class="t-numero">{{ evento.intensidade }}</b>
              </span>
              <span v-if="evento.descricao" class="agenda__descricao">{{ evento.descricao }}</span>
            </span>

            <span class="agenda__prazo">
              <span class="agenda__datas t-numero">{{ evento.periodo }}</span>
              <span v-if="evento.aviso" class="agenda__aviso">{{ evento.aviso }}</span>
            </span>
          </button>
        </li>
      </ul>
    </section>

    <!-- A legenda da barra colorida: sem ela, a cor da aresta é decoração. -->
    <p v-if="eventos.length" class="agenda__legenda">
      <span class="agenda__chave agenda__chave--good">{{ t('events.calendar.legend.good') }}</span>
      <span class="agenda__chave agenda__chave--bad">{{ t('events.calendar.legend.bad') }}</span>
      <span class="agenda__chave agenda__chave--neutral">{{ t('events.calendar.legend.neutral') }}</span>
    </p>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { getItemImage } from '../utils/eventImages';
import { FishIcon } from '@/shared/ui';

const UM_DIA = 86400000;

/** Efeito que TIRA alguma coisa: o número dele se lê com sinal de menos. */
const EFEITOS_QUE_TIRAM = [
  'divideFishingPower',
  'divideCatchRate',
  'lessGold',
  'morePricesBait',
  'morePricesRod',
  'morePricesBoat',
];

/** Efeito sem intensidade: o número seria sempre 0 e o chip só faria sujeira. */
const EFEITOS_SEM_NUMERO = ['blockedMap', 'noRest', 'station', 'legendaryShoal'];

export default {
  name: 'CalendarEvents',
  components: { FishIcon },
  emits: ['selecionar'],
  setup(_, { emit }) {
    const { t, locale } = useI18n();
    const store = useStore();
    const currentDate = ref(new Date());
    const events = ref({});
    const abertoId = ref(null);

    const nomeDoMes = computed(() =>
      currentDate.value.toLocaleString(locale.value, { month: 'long' }),
    );
    const ano = computed(() => currentDate.value.getFullYear());
    const dataDeHoje = computed(() =>
      new Date().toLocaleDateString(locale.value, { weekday: 'short', day: 'numeric', month: 'short' }),
    );

    const meiaNoite = (valor) => {
      const d = new Date(valor);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    /** Dias inteiros entre duas meias-noites — a conta que a agenda mostra. */
    const emDias = (de, ate) => Math.round((meiaNoite(ate) - meiaNoite(de)) / UM_DIA);

    /**
     * Os eventos do mês, UM por evento.
     *
     * A rota devolve `{ [diaDoMes]: [entrada] }` e repete a mesma entrada em cada dia que ela
     * cobre — é o formato que o Strapi legou. Aqui isso volta a ser uma lista de eventos, que é o
     * que a página mostra: o período já diz quais dias são.
     */
    const eventos = computed(() => {
      const hoje = meiaNoite(new Date());
      const porId = new Map();

      for (const entradas of Object.values(events.value)) {
        for (const entrada of entradas || []) {
          if (!entrada.event?.publishedAt) continue;
          if (!porId.has(entrada.id)) porId.set(entrada.id, entrada);
        }
      }

      const dia = (valor) =>
        new Date(valor).toLocaleDateString(locale.value, { day: '2-digit', month: '2-digit' });

      return [...porId.values()]
        .map((entrada) => {
          const inicio = meiaNoite(entrada.createdAt);
          const fim = meiaNoite(entrada.endDate);
          const comecou = inicio <= hoje;
          const acabou = fim < hoje;
          const estado = acabou ? 'past' : comecou ? 'active' : 'upcoming';

          return {
            id: entrada.id,
            entrada,
            nome: traduzido(entrada.event.name, 'name', entrada.event.name),
            descricao: traduzido(
              entrada.event.name,
              'description',
              entrada.event.description || '',
            ),
            tipo: entrada.event.typeEvent || 'neutral',
            estado,
            inicio,
            efeito: rotuloDoEfeito(entrada.event.effectType),
            intensidade: intensidade(entrada.event),
            periodo: `${dia(inicio)} – ${dia(fim)}`,
            aviso: aviso(estado, inicio, fim, hoje),
          };
        })
        .sort((a, b) => a.inicio - b.inicio);
    });

    /**
     * O nome e a descrição do evento no idioma de quem lê.
     *
     * O calendário mostrava o texto **cru do banco** — português, nos quatro idiomas —, enquanto o
     * modal da Home já traduzia pelo mesmo dicionário (`events.<nome>.name`). Eram duas telas do
     * mesmo evento em línguas diferentes.
     *
     * O fallback é o texto do banco, e não a chave: evento recém-criado no painel ainda não tem
     * tradução, e é melhor ele aparecer em português do que aparecer como uma chave crua no meio
     * da agenda. É a mesma escolha do `rotuloDoEfeito`, logo abaixo.
     */
    function traduzido(nomeDoEvento, campo, padrao) {
      if (!nomeDoEvento) return padrao;
      const chave = `events.${nomeDoEvento}.${campo}`;
      const texto = t(chave);
      return texto === chave ? padrao : texto;
    }

    function rotuloDoEfeito(tipo) {
      if (!tipo) return '';
      const chave = `events.calendar.effectTypes.${tipo}`;
      const texto = t(chave);
      // Efeito novo no backend ainda sem tradução: mostra o próprio tipo, não a chave crua.
      return texto === chave ? tipo : texto;
    }

    function intensidade(evento) {
      const numero = evento.effectNumber;
      if (!numero || EFEITOS_SEM_NUMERO.includes(evento.effectType)) return '';
      return `${EFEITOS_QUE_TIRAM.includes(evento.effectType) ? '−' : '+'}${numero}%`;
    }

    function aviso(estado, inicio, fim, hoje) {
      if (estado === 'past') return '';
      if (estado === 'active') {
        const faltam = emDias(hoje, fim);
        return faltam <= 0 ? t('events.calendar.lastDay') : t('events.calendar.daysLeft', { dias: faltam });
      }
      const faltam = emDias(hoje, inicio);
      return faltam <= 1
        ? t('events.calendar.startsTomorrow')
        : t('events.calendar.startsIn', { dias: faltam });
    }

    /** As três seções, na ordem em que interessam. Seção vazia não aparece. */
    const grupos = computed(() =>
      [
        { chave: 'active', rotulo: t('events.calendar.active') },
        { chave: 'upcoming', rotulo: t('events.calendar.upcoming') },
        { chave: 'past', rotulo: t('events.calendar.past') },
      ]
        .map((grupo) => ({ ...grupo, itens: eventos.value.filter((e) => e.estado === grupo.chave) }))
        .filter((grupo) => grupo.itens.length > 0),
    );

    const abrir = (evento) => {
      abertoId.value = evento.id;
      emit('selecionar', evento.entrada);
    };

    const fetchEvents = async () => {
      try {
        // Via store, e não axios direto: a action fala com o V2 pelo service e devolve o envelope
        // `{ data: { [diaDoMes]: [entrada] } }`.
        const resultado = await store.dispatch('events/fetchMonthEvents', {
          month: currentDate.value.getMonth() + 1,
          year: currentDate.value.getFullYear(),
        });
        events.value = resultado.data || {};
      } catch (error) {
        console.error(
          'Error fetching events:',
          error.response?.data?.message || error.response?.data?.error?.message || error.message,
        );
      }
    };

    const mesAnterior = () => {
      currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() - 1));
      fetchEvents();
    };

    const proximoMes = () => {
      currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() + 1));
      fetchEvents();
    };

    onMounted(fetchEvents);

    return {
      t,
      nomeDoMes,
      ano,
      dataDeHoje,
      eventos,
      grupos,
      abertoId,
      abrir,
      mesAnterior,
      proximoMes,
      getItemImage,
    };
  },
};
</script>

<style scoped>
.agenda {
  font-family: var(--fonte-corpo);
  color: var(--tinta);
}

/* -------------------------------------------------------------------------------------------- *
 * O CABEÇALHO — o mês, e as duas setas que trocam de mês.
 * -------------------------------------------------------------------------------------------- */
.agenda__mes {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--e-2);
  padding-bottom: var(--e-1);
  border-bottom: 2px solid var(--papel-sombra);
}

.agenda__mes h2 {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-4);
  font-weight: 400;
  color: var(--tinta);
  text-transform: capitalize;
  text-align: center;
}

.agenda__ano { color: var(--tinta-fraca); }

/* A seta é ALVO: era um ícone solto de 24px sem caixa, e no telefone não se acertava. */
.agenda__nav {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--papel);
  border: 2px solid var(--papel-sombra);
  color: var(--tinta);
  cursor: pointer;
  transition: border-color var(--t-rapido) var(--curva-suave),
    color var(--t-rapido) var(--curva-suave);
}

@media (hover: hover) {
  .agenda__nav:hover {
    border-color: var(--latao);
    color: var(--latao);
  }
}

.agenda__nav:active { transform: translateY(1px); }

/* A linha de hoje: a data por extenso, a lápis, logo abaixo do mês. */
.agenda__hoje {
  margin: var(--e-2) 0 var(--e-3);
  display: flex;
  align-items: center;
  gap: var(--e-1);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

/* Só a primeira letra: `capitalize` na linha inteira produzia "Sex., 4 De Set." — o "De" maiúsculo
 * é o `toLocaleDateString` do português entregando "de" no meio da data. */
.agenda__hoje strong {
  color: var(--latao);
}

.agenda__hoje strong::first-letter { text-transform: uppercase; }

/* -------------------------------------------------------------------------------------------- *
 * AS SEÇÕES
 * -------------------------------------------------------------------------------------------- */
.agenda__grupo + .agenda__grupo { margin-top: var(--e-4); }

.agenda__secao {
  margin: 0 0 var(--e-2);
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--e-2);
  padding-bottom: 2px;
  border-bottom: 1px dashed var(--papel-sombra);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.agenda__contagem { color: var(--latao); }

.agenda__grupo ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--e-2);
}

/* -------------------------------------------------------------------------------------------- *
 * O CARTÃO DO EVENTO — a marcação a tinta, com a cor só na barra lateral (D0).
 * -------------------------------------------------------------------------------------------- */
.agenda__cartao {
  display: flex;
  align-items: center;
  gap: var(--e-2);
  width: 100%;
  padding: var(--e-2);
  background: var(--papel-luz);
  border: 2px solid var(--papel-sombra);
  border-left: 4px solid var(--cor-neutra);
  color: var(--tinta);
  font-family: var(--fonte-corpo);
  text-align: left;
  cursor: pointer;
  transition: border-color var(--t-rapido) var(--curva-suave),
    transform var(--t-rapido) var(--curva-suave);
}

.agenda__cartao--good { border-left-color: var(--cor-bom); }
.agenda__cartao--bad { border-left-color: var(--cor-ruim); }

@media (hover: hover) {
  .agenda__cartao:hover {
    border-color: var(--latao);
    transform: translateX(2px);
  }
  .agenda__cartao:hover.agenda__cartao--good { border-left-color: var(--cor-bom); }
  .agenda__cartao:hover.agenda__cartao--bad { border-left-color: var(--cor-ruim); }
}

/* O que está aberto na página ao lado fica marcado: sem isso, clicar não muda nada aqui. */
.agenda__cartao--aberto {
  border-color: var(--latao);
  box-shadow: var(--sombra-1);
}

.agenda__cartao--passado { opacity: 0.5; }

/* A arte emoldurada: o evento vem com foto, e foto sem moldura no papel parece recorte solto. */
.agenda__arte {
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--papel);
  border: 2px solid var(--papel-sombra);
  overflow: hidden;
}

.agenda__arte img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  image-rendering: auto;
}

.agenda__texto {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* O nome QUEBRA em vez de cortar: "Bloqueio da ⋯" no telefone escondia justamente qual bloqueio. */
.agenda__nome {
  font-family: var(--fonte-display);
  font-size: var(--f-2);
  line-height: 1.2;
}

.agenda__efeito {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agenda__efeito b { color: var(--tinta); }

/* A descrição do evento, em duas linhas. Cabe porque o backend passou a mandá-la na agenda; sem
 * ela o cartão tinha nome e nada mais, e a página inteira ficava com três dedos de altura. */
.agenda__descricao {
  margin-top: 2px;
  font-size: var(--f-1);
  line-height: 1.35;
  color: var(--tinta-lapis);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.agenda__prazo {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  font-size: var(--f-1);
}

.agenda__datas { color: var(--tinta); }

.agenda__aviso {
  color: var(--latao);
  text-align: right;
}

.agenda__cartao--passado .agenda__aviso { color: var(--tinta-fraca); }

/* No telefone o cartão vira dois andares: em 390px, três colunas espremiam o nome até o "⋯". */
@media (max-width: 599px) {
  .agenda__cartao { flex-wrap: wrap; }

  .agenda__prazo {
    width: 100%;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--e-2);
    padding-top: var(--e-1);
    border-top: 1px dashed var(--papel-sombra);
  }
}

/* -------------------------------------------------------------------------------------------- *
 * A LEGENDA
 * -------------------------------------------------------------------------------------------- */
.agenda__legenda {
  margin: var(--e-4) 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--e-1) var(--e-3);
  padding-top: var(--e-2);
  border-top: 1px dashed var(--papel-sombra);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.agenda__chave {
  display: inline-flex;
  align-items: center;
  gap: var(--e-1);
}

.agenda__chave::before {
  content: '';
  width: 4px;
  height: 12px;
  background: var(--cor-neutra);
}

.agenda__chave--good::before { background: var(--cor-bom); }
.agenda__chave--bad::before { background: var(--cor-ruim); }

/* -------------------------------------------------------------------------------------------- *
 * O VAZIO — moldura pontilhada, não uma frase solta em itálico.
 * -------------------------------------------------------------------------------------------- */
.agenda__vazio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--e-1);
  padding: var(--e-5) var(--e-3);
  border: 2px dashed var(--papel-sombra);
  color: var(--tinta-fraca);
  text-align: center;
}

.agenda__vazio p {
  margin: 0;
  font-size: var(--f-2);
}
</style>
